// components/OBE/CourseOutcome.jsx
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { FiBook, FiGitBranch, FiCalendar, FiPlus, FiTrash2, FiEdit, FiFilter, FiAlertCircle } from "react-icons/fi";
import { baseApiURL } from "../../../baseUrl";


const CourseOutcome = ({ branch: lockedBranchName }) => {
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    branch: "",
    semester: "",
    regulation: ""
  });
  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newCO, setNewCO] = useState({ coNumber: "", description: "" });
  const [editMode, setEditMode] = useState(false);
  const [editingCO, setEditingCO] = useState(null);
  const [noStudentsMessage, setNoStudentsMessage] = useState("");
  const [studentRegulations, setStudentRegulations] = useState([]);

  useEffect(() => {
    getSubjectsHandler();
    getBranchesHandler();
    getStudentRegulationsHandler();
  }, []);

  const getStudentRegulationsHandler = () => {
    axios
      .get(`${baseApiURL()}/student/details/getRegulations`)
      .then((response) => {
        if (response.data.success) {
          setStudentRegulations(response.data.regulations || []);
        }
      })
      .catch((error) => console.error("Error fetching student regulations:", error));
  };

  useEffect(() => {
    filterSubjects();
  }, [subjects, filters]);

  useEffect(() => {
    checkStudentPresence();
  }, [filters.semester, filters.branch, branches, selectedSubject]);

  const checkStudentPresence = async () => {
    const targetSemester = filters.semester || selectedSubject?.semester;
    if (!targetSemester) {
      setNoStudentsMessage("");
      return;
    }

    let branchName = "";
    if (filters.branch) {
      const branchObj = branches.find(b => b._id === filters.branch || b.name === filters.branch);
      if (branchObj?.name) branchName = branchObj.name;
    } else if (selectedSubject?.branch) {
      branchName = selectedSubject.branch?.name || selectedSubject.branch;
    }

    if (!branchName) {
      setNoStudentsMessage("");
      return;
    }

    try {
      const response = await axios.post(`${baseApiURL()}/student/details/getCohortRegulation`, {
        branch: branchName,
        semester: Number(targetSemester)
      });

      if (response.data.success && response.data.count > 0 && response.data.regulations?.length > 0) {
        const autoReg = response.data.regulations[0]; // e.g. R22
        setFilters(prev => ({ ...prev, regulation: autoReg }));
        setNoStudentsMessage("");
      } else {
        setFilters(prev => ({ ...prev, regulation: "" }));
        setNoStudentsMessage("no student in that semester");
      }
    } catch (error) {
      console.error("Error detecting cohort regulation:", error);
      setFilters(prev => ({ ...prev, regulation: "" }));
      setNoStudentsMessage("no student in that semester");
    }
  };

  const getBranchesHandler = () => {
    axios
      .get(`${baseApiURL()}/branch/getBranch`)
      .then((response) => {
        if (response.data.success) {
          setBranches(response.data.branches);
          if (lockedBranchName) {
            const matchedBranch = response.data.branches.find(b => b.name === lockedBranchName);
            if (matchedBranch) {
              setFilters(prev => ({ ...prev, branch: matchedBranch._id }));
            }
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching branches:", error);
      });
  };

  const filterSubjects = () => {
    if (noStudentsMessage) {
      setFilteredSubjects([]);
      return;
    }
    let filtered = [...subjects];
    
    if (filters.branch) {
      filtered = filtered.filter(item => {
        const branchId = item.branch?._id || item.branch;
        return branchId === filters.branch;
      });
    }
    
    if (filters.semester) {
      filtered = filtered.filter(item => 
        String(item.semester) === String(filters.semester)
      );
    }

    if (filters.regulation) {
      filtered = filtered.filter(item => 
        item.regulation && item.regulation.toUpperCase() === filters.regulation.toUpperCase()
      );
    }
    
    setFilteredSubjects(filtered);
  };

  const clearFilters = () => {
    setFilters({
      branch: lockedBranchName ? filters.branch : "",
      semester: "",
      regulation: ""
    });
  };

  const getSubjectsHandler = () => {
    setLoading(true);
    axios
      .get(`${baseApiURL()}/subject/getSubject`)
      .then((response) => {
        if (response.data.success) {
          setSubjects(response.data.subject);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setEditMode(false);
    setEditingCO(null);
    setNewCO({ coNumber: "", description: "" });
  };

  const handleAddCourseOutcome = (e) => {
    e.preventDefault();
    
    if (!newCO.coNumber || !newCO.description) {
      toast.error("Please fill all fields");
      return;
    }

    if (!selectedSubject) {
      toast.error("Please select a subject first");
      return;
    }

    setLoading(true);
    axios
      .post(`${baseApiURL()}/subject/addCourseOutcome/${selectedSubject._id}`, newCO)
      .then((response) => {
        if (response.data.success) {
          toast.success(response.data.message);
          getSubjectsHandler();
          setNewCO({ coNumber: "", description: "" });
          
          // Update selected subject with new data
          const updatedSubjects = subjects.map(sub => 
            sub._id === selectedSubject._id ? response.data.subject : sub
          );
          setSubjects(updatedSubjects);
          setSelectedSubject(response.data.subject);
          setEditMode(false);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        toast.error(error.response?.data?.message || error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEditCourseOutcome = (co) => {
    setEditMode(true);
    setEditingCO(co);
    setNewCO({
      coNumber: co.coNumber,
      description: co.description
    });
  };

  const handleDeleteCourseOutcome = (coNumber) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete ${coNumber}?`);
    if (!confirmDelete) return;

    if (!selectedSubject) return;

    setLoading(true);
    axios
      .delete(`${baseApiURL()}/subject/deleteCourseOutcome/${selectedSubject._id}/${coNumber}`)
      .then((response) => {
        if (response.data.success) {
          toast.success(response.data.message);
          getSubjectsHandler();
          
          // Update selected subject with new data
          const updatedSubjects = subjects.map(sub => 
            sub._id === selectedSubject._id ? response.data.subject : sub
          );
          setSubjects(updatedSubjects);
          setSelectedSubject(response.data.subject);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        toast.error(error.response?.data?.message || error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditingCO(null);
    setNewCO({ coNumber: "", description: "" });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bento-header-banner flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Course Outcomes Management</h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-xs mt-0.5">Define and manage Course Outcomes for subjects</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Subject Selection */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-5 space-y-4">
            <div className="flex items-center space-x-2">
              <FiBook className="text-indigo-600 dark:text-indigo-400 text-base" />
              <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider">Select Subject</h2>
            </div>

            {/* Filters Section */}
            <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FiFilter className="text-indigo-600 dark:text-indigo-400 text-xs" />
                  <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Filter Subjects</span>
                </div>
                {(filters.branch || filters.semester || filters.regulation) && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-semibold cursor-pointer"
                  >
                    Clear All
                  </button>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Filter by Branch</label>
                <select
                  value={filters.branch}
                  onChange={(e) => setFilters({ ...filters, branch: e.target.value })}
                  disabled={!!lockedBranchName}
                  className={`w-full px-3 py-2 text-xs font-medium border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white ${lockedBranchName ? 'opacity-50 cursor-not-allowed bg-slate-100 dark:bg-slate-800' : 'bg-white dark:bg-slate-900'}`}
                >
                  <option value="">All Branches</option>
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Filter by Semester</label>
                <select
                  value={filters.semester}
                  onChange={(e) => setFilters({ ...filters, semester: e.target.value })}
                  className="w-full px-3 py-2 text-xs font-medium border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                >
                  <option value="">All Semesters</option>
                  {semesters.map((sem) => (
                    <option key={sem} value={sem}>
                      Semester {sem}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">Filter by Regulation</label>
                  {filters.regulation && (
                    <span className="text-[10px] bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded font-bold border border-indigo-100 dark:border-indigo-800">
                      Auto-fetched
                    </span>
                  )}
                </div>
                <select
                  value={filters.regulation}
                  onChange={(e) => setFilters({ ...filters, regulation: e.target.value })}
                  className="w-full px-3 py-2 text-xs font-medium border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                >
                  <option value="">All Regulations</option>
                  {(studentRegulations.length > 0 ? studentRegulations : Array.from(new Set(subjects.map(s => s.regulation).filter(Boolean)))).map((reg) => (
                    <option key={reg} value={reg}>
                      {reg}
                    </option>
                  ))}
                </select>
              </div>

              <div className="text-[11px] text-slate-400 dark:text-slate-500 pt-1 font-medium">
                Showing {filteredSubjects.length} of {subjects.length} subjects
              </div>

              {noStudentsMessage && (
                <div className="mt-2 p-2.5 bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800 rounded-xl text-amber-800 dark:text-amber-300 text-xs flex items-center space-x-2">
                  <FiAlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                  <span className="font-semibold">{noStudentsMessage}</span>
                </div>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center items-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <div className="space-y-2.5 max-h-[500px] overflow-y-auto pr-1">
                {filteredSubjects.map((subject) => (
                  <div
                    key={subject._id}
                    className={`p-3.5 rounded-xl border cursor-pointer transition ${
                      selectedSubject?._id === subject._id
                        ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/30'
                        : 'border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-950/40'
                    }`}
                    onClick={() => handleSubjectSelect(subject)}
                  >
                    <h3 className="font-bold text-xs text-slate-900 dark:text-white">{subject.code} - {subject.name}</h3>
                    <div className="flex items-center space-x-3 mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                      <div className="flex items-center space-x-1">
                        <FiGitBranch className="text-emerald-500" />
                        <span>{subject.branch?.name || 'N/A'}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <FiCalendar className="text-indigo-500" />
                        <span>Sem {subject.semester}</span>
                      </div>
                      {subject.regulation && (
                        <span className="bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded text-[10px] font-bold border border-indigo-100 dark:border-indigo-800">
                          {subject.regulation}
                        </span>
                      )}
                    </div>
                    <div className="mt-2">
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg">
                        {subject.courseOutcomes?.length || 0} COs
                      </span>
                    </div>
                  </div>
                ))}
                
                {filteredSubjects.length === 0 && (
                  <div className="text-center py-8">
                    <FiBook className="w-10 h-10 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                    <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">No subjects found matching your filters.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Course Outcomes */}
        <div className="lg:col-span-2">
          {selectedSubject ? (
            <div className="space-y-6">
              {noStudentsMessage && (
                <div className="p-3.5 bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800 rounded-xl flex items-center space-x-3 text-amber-800 dark:text-amber-300 shadow-xs">
                  <FiAlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">Notice</p>
                    <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">{noStudentsMessage}</p>
                  </div>
                </div>
              )}

              {/* Subject Info */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-bold text-slate-900 dark:text-white">{selectedSubject.code} - {selectedSubject.name}</h2>
                    <div className="flex items-center space-x-4 mt-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium">
                      <span>
                        <FiGitBranch className="inline mr-1 text-emerald-500" />
                        {selectedSubject.branch?.name}
                      </span>
                      <span>
                        <FiCalendar className="inline mr-1 text-indigo-500" />
                        Semester {selectedSubject.semester}
                      </span>
                      {selectedSubject.regulation && (
                        <span className="bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded text-[10px] font-bold border border-indigo-100 dark:border-indigo-800">
                          {selectedSubject.regulation}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-400 dark:text-slate-500 font-semibold">Total Course Outcomes</div>
                    <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                      {selectedSubject.courseOutcomes?.length || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Add/Edit Course Outcome Form */}
              <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-5 space-y-4">
                <div className="flex items-center space-x-2">
                  <FiPlus className="text-indigo-600 dark:text-indigo-400 text-base" />
                  <h2 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                    {editMode ? `Edit ${editingCO?.coNumber}` : 'Add New Course Outcome'}
                  </h2>
                </div>

                <form onSubmit={handleAddCourseOutcome}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-3">
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                        CO Number *
                      </label>
                      <input
                        type="text"
                        value={newCO.coNumber}
                        onChange={(e) => setNewCO({...newCO, coNumber: e.target.value})}
                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50"
                        placeholder="CO1"
                        required
                        disabled={editMode}
                      />
                    </div>
                    <div className="md:col-span-7">
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                        Description *
                      </label>
                      <input
                        type="text"
                        value={newCO.description}
                        onChange={(e) => setNewCO({...newCO, description: e.target.value})}
                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                        placeholder="Describe the course outcome..."
                        required
                      />
                    </div>
                    <div className="md:col-span-2 flex items-end">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 rounded-xl text-xs font-bold transition shadow-xs disabled:opacity-50 cursor-pointer"
                      >
                        {loading ? 'Saving...' : editMode ? 'Update' : 'Add'}
                      </button>
                    </div>
                  </div>
                  {editMode && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-semibold px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg cursor-pointer"
                      >
                        Cancel Edit
                      </button>
                    </div>
                  )}
                </form>
              </div>

              {/* Course Outcomes List */}
              <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-5 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                  <h2 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Course Outcomes List</h2>
                  <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                    Total: {selectedSubject.courseOutcomes?.length || 0}
                  </span>
                </div>

                {selectedSubject.courseOutcomes?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedSubject.courseOutcomes.map((co) => (
                      <div
                        key={co.coNumber}
                        className="p-3.5 border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-950/40 hover:bg-slate-100/50 dark:hover:bg-slate-800/40 transition"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-start space-x-3">
                              <span className="inline-flex items-center justify-center w-9 h-9 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800 rounded-xl font-bold text-xs shrink-0">
                                {co.coNumber}
                              </span>
                              <div>
                                <p className="text-xs font-medium text-slate-900 dark:text-white leading-relaxed">{co.description}</p>
                                <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
                                  Course Outcome for {selectedSubject.name}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="flex space-x-1 ml-2">
                            <button
                              onClick={() => handleEditCourseOutcome(co)}
                              className="p-1.5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 rounded-lg transition cursor-pointer"
                              title="Edit"
                            >
                              <FiEdit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteCourseOutcome(co.coNumber)}
                              className="p-1.5 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/60 rounded-lg transition cursor-pointer"
                              title="Delete"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <FiBook className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                    <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">No Course Outcomes</h3>
                    <p className="text-xs text-slate-400 dark:text-slate-500">Add your first course outcome using the form above</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-12 text-center">
              <FiBook className="w-16 h-16 text-slate-300 dark:text-slate-700 mx-auto mb-4" />
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-200 mb-1">Select a Subject</h3>
              <p className="text-xs text-slate-400 dark:text-slate-500 max-w-md mx-auto">
                Choose a subject from the list to view and manage its Course Outcomes.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseOutcome;